'use client';

import { useState } from 'react';
import { BookingActionBar } from './BookingActionBar';
import type { BookingTypeSummary, CreateBookingResponse, SlotApi } from './types';

const FIELD_STYLE: React.CSSProperties = {
  width: '100%',
  background: 'var(--bg-elevated)',
  borderWidth: 1,
  borderStyle: 'solid',
  borderColor: 'var(--border)',
  borderRadius: 'var(--radius-md)',
  padding: '14px 16px',
  color: 'var(--text-primary)',
  fontFamily: 'var(--font-mono), ui-monospace, monospace',
  fontSize: 13,
  lineHeight: 1.5,
};

const LABEL_STYLE: React.CSSProperties = {
  display: 'block',
  marginBottom: 8,
  color: 'var(--text-muted)',
  fontFamily: 'var(--font-mono), ui-monospace, monospace',
  fontSize: 11,
  fontWeight: 600,
  letterSpacing: '0.06em',
  textTransform: 'uppercase',
};

type Props = {
  bookingType: BookingTypeSummary;
  slot: SlotApi;
  clientTimezone: string;
  initialPrefill?: { name?: string; email?: string };
  onSuccess: (response: CreateBookingResponse) => void;
};

export function BookingDetailsForm({ bookingType, slot, clientTimezone, initialPrefill, onSuccess }: Props) {
  const [name, setName] = useState(initialPrefill?.name ?? '');
  const [email, setEmail] = useState(initialPrefill?.email ?? '');
  const [phone, setPhone] = useState('');
  const [company, setCompany] = useState('');
  const [role, setRole] = useState('');
  const [notes, setNotes] = useState('');
  const [website, setWebsite] = useState('');

  const [status, setStatus] = useState<'idle' | 'submitting' | 'error'>('idle');
  const [error, setError] = useState<string | null>(null);
  const [errors, setErrors] = useState<Record<string, string>>({});

  function validate(): boolean {
    const e: Record<string, string> = {};
    if (!name.trim()) e.name = 'Name is required';
    if (!email.trim()) e.email = 'Email is required';
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim())) e.email = 'Enter a valid email';
    // Required: this is the whole point of the call, and what a quote is built from.
    if (!notes.trim()) e.notes = 'Tell me a little about what you need';
    setErrors(e);
    return Object.keys(e).length === 0;
  }

  async function onSubmit(ev: React.FormEvent<HTMLFormElement>) {
    ev.preventDefault();
    if (!validate()) return;
    setStatus('submitting');
    setError(null);

    try {
      const res = await fetch('/api/bookings', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          slug: bookingType.slug,
          startUtc: slot.startUtc,
          clientName: name.trim(),
          clientEmail: email.trim(),
          clientPhone: phone.trim() || undefined,
          clientCompany: company.trim() || undefined,
          clientRole: role.trim() || undefined,
          clientNotes: notes.trim() || undefined,
          clientTimezone,
          website,
        }),
      });
      if (!res.ok) {
        const data = (await res.json().catch(() => ({}))) as { error?: string };
        if (data.error === 'SLOT_TAKEN') {
          throw new Error('That time was just taken. Please pick another.');
        }
        throw new Error(data.error ?? 'Something went wrong');
      }
      const data = (await res.json()) as CreateBookingResponse;
      if (data.requiresPayment) {
        window.location.href = data.stripeCheckoutUrl;
        return;
      }
      onSuccess(data);
    } catch (err) {
      setStatus('error');
      setError(err instanceof Error ? err.message : 'Something went wrong');
    }
  }

  return (
    <form onSubmit={onSubmit} className="flex flex-col gap-5" noValidate>
      {/* Honeypot */}
      <div aria-hidden="true" style={{ position: 'absolute', left: -9999, width: 1, height: 1, overflow: 'hidden' }}>
        <label htmlFor="website-hp">Website</label>
        <input
          id="website-hp"
          type="text"
          autoComplete="off"
          tabIndex={-1}
          value={website}
          onChange={(e) => setWebsite(e.target.value)}
        />
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
        <div>
          <label htmlFor="name" style={LABEL_STYLE}>Name</label>
          <input
            id="name"
            type="text"
            placeholder="Jane Founder"
            className="contact-field"
            style={{ ...FIELD_STYLE, borderColor: errors.name ? 'var(--rose)' : FIELD_STYLE.borderColor }}
            value={name}
            onChange={(e) => setName(e.target.value)}
            required
            autoComplete="name"
          />
          {errors.name && <p className="mt-1.5 font-mono" style={{ color: 'var(--rose)', fontSize: 11 }}>{errors.name}</p>}
        </div>
        <div>
          <label htmlFor="email" style={LABEL_STYLE}>Email</label>
          <input
            id="email"
            type="email"
            placeholder="jane@example.com"
            className="contact-field"
            style={{ ...FIELD_STYLE, borderColor: errors.email ? 'var(--rose)' : FIELD_STYLE.borderColor }}
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
            autoComplete="email"
          />
          {errors.email && <p className="mt-1.5 font-mono" style={{ color: 'var(--rose)', fontSize: 11 }}>{errors.email}</p>}
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
        <div>
          <label htmlFor="phone" style={LABEL_STYLE}>Phone (optional)</label>
          <input
            id="phone"
            type="tel"
            placeholder="+1 (555) 123-4567"
            className="contact-field"
            style={FIELD_STYLE}
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
            autoComplete="tel"
          />
        </div>
        <div>
          <label htmlFor="company" style={LABEL_STYLE}>Company (optional)</label>
          <input
            id="company"
            type="text"
            placeholder="Acme, Inc."
            className="contact-field"
            style={FIELD_STYLE}
            value={company}
            onChange={(e) => setCompany(e.target.value)}
            autoComplete="organization"
          />
        </div>
      </div>

      <div>
        <label htmlFor="role" style={LABEL_STYLE}>Role (optional)</label>
        <input
          id="role"
          type="text"
          placeholder="Founder · CTO · PM"
          className="contact-field"
          style={FIELD_STYLE}
          value={role}
          onChange={(e) => setRole(e.target.value)}
        />
      </div>

      <div>
        <label htmlFor="notes" style={LABEL_STYLE}>How can I help?</label>
        <textarea
          id="notes"
          rows={4}
          placeholder="Tell me about the work: what you are building, a problem you are stuck on, or something you would like a second opinion on. Rough is fine."
          className="contact-field"
          style={{
            ...FIELD_STYLE,
            resize: 'vertical',
            minHeight: 110,
            borderColor: errors.notes ? 'var(--rose)' : FIELD_STYLE.borderColor,
          }}
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          maxLength={1500}
          required
        />
        {errors.notes && (
          <p className="mt-1.5 font-mono" style={{ color: 'var(--rose)', fontSize: 11 }}>{errors.notes}</p>
        )}
        <p className="mt-2 text-text-muted" style={{ fontSize: 12, lineHeight: 1.5 }}>
          A couple of sentences is enough. It is what I use to prepare, and to put a price together
          if the work goes further.
        </p>
      </div>

      {error && status === 'error' && (
        <p className="font-mono" style={{ color: 'var(--rose)', fontSize: 12 }}>{error}</p>
      )}

      <BookingActionBar inFlowMarginTop={0}>
        <button
          type="submit"
          disabled={status === 'submitting'}
          className="cta-primary inline-flex items-center justify-center transition-all duration-300 disabled:opacity-60 disabled:cursor-not-allowed"
          style={{
            background: 'linear-gradient(135deg, var(--accent), var(--indigo))',
            color: 'var(--bg-primary)',
            fontSize: 15,
            fontWeight: 800,
            padding: '15px 32px',
            borderRadius: 'var(--radius-lg)',
          }}
        >
          {status === 'submitting'
            ? 'Confirming…'
            : bookingType.depositRequired
            ? `Continue to payment →`
            : `Confirm booking →`}
        </button>
      </BookingActionBar>
    </form>
  );
}
